import Dexie from 'dexie';
import { buildSnapshot, encodeSnapshot, decodeSnapshot, isEmptySnapshot } from './snapshot.js';
import { dayKey } from '../scheduler/day.js';

// Snapshots live in a separate IndexedDB database so that a corrupted or
// wiped main database never takes its own backups with it.
let store = null;

export function backupStore(name = 'recall-backups') {
  if (!store || store.name !== name) {
    store = new Dexie(name);
    store.version(1).stores({ snapshots: '++id, createdAt, reason', blobs: 'id' });
  }
  return store;
}

export async function deleteAllLocalSnapshots() {
  await backupStore().delete();
  store = null;
}

const AUTO = 'daily';
const KEEP_SAFETY = 10;
const KEEP_DAILY = 7;
const KEEP_WEEKLY = 4;

function weekKey(ms) {
  const d = new Date(ms);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return dayKey(d.getTime());
}

export async function createLocalSnapshot(db, reason = 'manual', { skipIfEmpty = false } = {}) {
  const snap = await buildSnapshot(db, { reason });
  if (skipIfEmpty && isEmptySnapshot(snap)) return null;
  const bytes = encodeSnapshot(snap);
  const s = backupStore();
  const id = await s.transaction('rw', s.snapshots, s.blobs, async () => {
    const newId = await s.snapshots.add({
      createdAt: snap.createdAt,
      reason,
      day: dayKey(snap.createdAt),
      counts: snap.counts,
      size: bytes.length,
      deviceId: snap.deviceId,
    });
    await s.blobs.put({ id: newId, bytes });
    return newId;
  });
  await pruneLocal();
  return id;
}

export function listLocalSnapshots() {
  return backupStore().snapshots.orderBy('createdAt').reverse().toArray();
}

export async function loadLocalSnapshot(id) {
  const blob = await backupStore().blobs.get(id);
  if (!blob) throw new Error('Snapshot data is missing.');
  return decodeSnapshot(blob.bytes);
}

export async function localSnapshotBytes(id) {
  return (await backupStore().blobs.get(id))?.bytes ?? null;
}

export async function deleteLocalSnapshot(id) {
  const s = backupStore();
  await s.transaction('rw', s.snapshots, s.blobs, async () => {
    await s.snapshots.delete(id);
    await s.blobs.delete(id);
  });
}

/** Keep 7 daily + 4 weekly automatic snapshots, and the 10 newest safety/manual ones. */
export async function pruneLocal() {
  const all = await listLocalSnapshots();
  const keep = new Set();
  const autos = all.filter((s) => s.reason === AUTO);
  const days = new Set();
  const weeks = new Set();
  for (const s of autos) {
    if (days.size < KEEP_DAILY && !days.has(s.day)) {
      days.add(s.day);
      keep.add(s.id);
    }
  }
  for (const s of autos) {
    const w = weekKey(s.createdAt);
    if (keep.has(s.id)) {
      weeks.add(w);
      continue;
    }
    if (weeks.size < KEEP_DAILY + KEEP_WEEKLY && !weeks.has(w)) {
      weeks.add(w);
      keep.add(s.id);
    }
  }
  all.filter((s) => s.reason !== AUTO).slice(0, KEEP_SAFETY).forEach((s) => keep.add(s.id));
  for (const s of all) if (!keep.has(s.id)) await deleteLocalSnapshot(s.id);
}

export async function maybeDailyLocalSnapshot(db) {
  const today = dayKey();
  const existing = await backupStore().snapshots.where('reason').equals(AUTO).toArray();
  if (existing.some((s) => s.day === today)) return null;
  return createLocalSnapshot(db, AUTO, { skipIfEmpty: true });
}
